import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { clientCadence } from "../lib/api";

/**
 * v0.6.12 — upcoming filing deadlines, on the calculation panel.
 *
 * The deadlines already existed, but only on the Clients list, as a badge whose
 * detail lived in a hover tooltip. This is the screen where an operator decides
 * WHICH PERIOD to run, so it is the screen where "Italy is due in one day" and
 * "this period is already finalised" actually change what they do.
 *
 * Deliberately read-only and deliberately quiet:
 *
 *  - it never blocks and never nags. It is context beside the client name, not
 *    a gate on the calculation.
 *  - the strip stays SHORT. It sits above the calculation controls, so every
 *    row it adds pushes them down. Hence the compact pill: the country code
 *    rather than a sentence, the period with its year dropped when the
 *    deadline falls in the same year, and tight leading between wrapped rows.
 *    On a full-width panel a client with nine registrations fits on one row;
 *    the old wording ("DE monthly Sep 2026 · 10 Oct · 11d") managed four.
 *  - EVERY jurisdiction and registration is shown. There is no "+n more" and
 *    no cap: an operator asking "what is due" wants the whole picture, and a
 *    hidden row is exactly the failure this module has already shipped three
 *    times (Italy's Q2, OSS Q4, IOSS). The pills are sized to fit the space
 *    instead of being truncated to fit it.
 *  - a fetch failure renders NOTHING rather than an error. A deadline strip
 *    that breaks the page it decorates would be worse than one that is absent,
 *    and the authoritative view is still the Clients list.
 *  - "finalised", not "filed" or "submitted" — the same wording the badge uses,
 *    for the same reason: all the data model records is that a calc for the
 *    period was frozen in this app. Only UK MTD actually posts to an authority.
 */

// The SAME visual language as the per-country pills shown after a calculation
// (CountryReturnTabs.wrapToneClasses) — rounded-xl, border-2, tone-coloured
// ground, COUNTRY in bold uppercase followed by tabular detail. An operator should
// recognise a country here as the same object they click there, so these are
// deliberately a quieter SIZE of the same thing rather than a second style.
//
// Two conscious departures, both because these pills are READ-ONLY:
//   * no hover-lift, no shadow, no cursor-pointer. Those say "click me" in
//     CountryReturnTabs, and clicking here does nothing.
//   * a far-off deadline uses the IDLE tone, not a colour. CountryReturnTabs
//     reserves colour for "the operator has an action here", and a deadline
//     five weeks out does not. Only amber and rose carry meaning on this strip.
//
// Teal is deliberately NOT reused: there it means a CREDIT position, and a
// deadline is not a credit.
const TONE = {
  red: "border-rose-500 dark:border-rose-500 bg-rose-50 dark:bg-rose-950/40 text-rose-900 dark:text-rose-50",
  amber: "border-amber-500 dark:border-amber-500 bg-amber-50 dark:bg-amber-950/30 text-amber-900 dark:text-amber-50",
  green: "border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200",
  done: "border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/60 text-slate-500 dark:text-slate-400",
};

/**
 * Split "IT Q2 2026" into its country badge and the rest, so the country can
 * carry the same bold-uppercase emphasis it has in CountryReturnTabs. OSS and
 * IOSS have no country_code and are their own head.
 */
const splitLabel = (d) => {
  const head = d.country_code || (d.kind === "IOSS" ? "IOSS"
                : d.kind === "OSS" ? "OSS" : "");
  const label = d.label || "";
  let tail = head && label.toUpperCase().startsWith(head.toUpperCase())
    ? label.slice(head.length).trim()
    : label;
  // Drop words the period itself already says. "DE monthly Sep 2026" ->
  // "Sep 2026" (a month named Sep is monthly), "UK VAT Q3 2026" -> "Q3 2026"
  // (everything on this strip is VAT). "annual", "H1" and "Q3" all survive,
  // because those DO tell the reader which period they are looking at.
  // Drop a leading token that just repeats the country. The UK return's label
  // is "UK VAT Q3 2026" while its country_code is GB, so the pill was reading
  // "GB · UK VAT Q3" — two names for the same country, side by side.
  tail = tail.replace(/^(UK|GB)\s+/i, "");
  tail = tail.replace(/^(monthly|VAT)\s+/i, "").replace(/^VAT\s+/i, "");
  return { head: head || label, tail: head ? tail : "" };
};

/**
 * Drop the period's year when the deadline falls in the same year, because the
 * pill then reads "Q2 · 30 Sep" and loses nothing. Keep it when they differ —
 * "Dec 2025 · 31 Jan" — where dropping it WOULD be ambiguous. This is what buys
 * the room to show every registration rather than a truncated four.
 */
const trimYear = (tail, dueIso) => {
  const dueYear = (dueIso || "").slice(0, 4);
  if (!dueYear) return tail;
  return tail.replace(new RegExp(`\\s*\\b${dueYear}\\b`), "").trim() || tail;
};

/** "30 Sep" — short, unambiguous, and not locale-dependent on the server. */
const shortDate = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
                  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  if (!y || !m || !d) return iso;
  return `${d} ${MONTHS[m - 1]}`;
};

/** "1d", "in 11d", "3d overdue" — the urgency, in as few characters as fit. */
const whenText = (d) => {
  if (d.filed) return "finalised";
  const n = d.days_until;
  if (n === null || n === undefined) return "";
  if (n < 0) return `${Math.abs(n)}d overdue`;
  if (n === 0) return "today";
  return `${n}d`;
};

export const ClientDeadlineStrip = ({ clientId }) => {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const reqRef = useRef(0);

  useEffect(() => {
    if (!clientId) { setRows([]); return; }
    // Guard against a stale response overwriting a newer one when the operator
    // switches client twice quickly — the same cancellation bug TraceDrawer
    // had (v0.6.6).
    const seq = ++reqRef.current;
    let alive = true;
    clientCadence(clientId)
      .then((res) => {
        if (!alive || seq !== reqRef.current) return;
        setRows(Array.isArray(res?.deadlines) ? res.deadlines : []);
      })
      .catch(() => { if (alive && seq === reqRef.current) setRows([]); });
    return () => { alive = false; };
  }, [clientId]);

  // ONE PILL PER REGISTRATION, showing its NEXT deadline.
  //
  // "Every jurisdiction and registration" means exactly that — not every
  // upcoming PERIOD of each one. The backend hands back several months of IOSS
  // and several quarters of OSS because the Clients-list badge lists them in
  // its tooltip, and taken literally that put three IOSS pills on this strip
  // (Aug, Sep, Oct) for a single registration, crowding out the countries
  // beside it. An operator looking at this panel wants to know what is next
  // for each place they are registered, once.
  //
  // Collapsed on (kind + country), keeping the soonest. A finalised period is
  // only shown where there is nothing outstanding for that registration —
  // otherwise the open one is the news.
  const dated = useMemo(() => {
    const withDate = rows.filter((r) => r.due_date);
    const best = new Map();
    for (const r of withDate) {
      const key = `${r.kind}:${r.country_code || ""}`;
      const held = best.get(key);
      if (!held) { best.set(key, r); continue; }
      // an outstanding row always beats a finalised one; otherwise, sooner wins
      if (held.filed && !r.filed) best.set(key, r);
      else if (held.filed === r.filed && r.due_date < held.due_date) {
        best.set(key, r);
      }
    }
    const kept = [...best.values()];
    return [...kept.filter((r) => !r.filed).sort((a, b) => a.due_date.localeCompare(b.due_date)),
            ...kept.filter((r) => r.filed).sort((a, b) => a.due_date.localeCompare(b.due_date))];
  }, [rows]);
  const shown = dated;

  if (!shown.length) return null;

  return (
    <ul
      data-testid="client-deadline-strip"
      aria-label={t("ui.calculate.deadline_strip_label", { defaultValue: "Upcoming filing deadlines" })}
      className="flex items-center gap-1.5 gap-y-1 flex-wrap mt-2 list-none p-0 m-0"
    >
      {shown.map((d) => {
        const { head, tail } = splitLabel(d);
        return (
          <li key={`${d.kind}-${d.label}-${d.due_date}`}>
            <span
              data-testid={`deadline-chip-${d.country_code || d.kind}`}
              data-tone={d.filed ? "done" : (d.severity || "amber")}
              title={
                `${d.label} · due ${d.due_date}`
                + (d.estimated ? "\nEstimated — no deadline rule on file for this return; it may be due earlier" : "")
                + (d.filed
                    ? `\nFinalised${d.filed_at ? " " + d.filed_at.slice(0, 10) : ""}`
                      + (d.filing_reference ? ` · ${d.filing_reference}` : "")
                    : "")
              }
              className={
                "inline-flex items-center gap-1 rounded-lg border-2 "
                + "px-2 py-0.5 whitespace-nowrap "
                + (d.filed ? TONE.done : (TONE[d.severity] || TONE.amber))
              }
            >
              <span className="font-bold uppercase tracking-wide text-[11px]">
                {head}
              </span>
              {tail && (
                <>
                  <span className="opacity-50" aria-hidden="true">·</span>
                  <span className="font-medium tabular-nums text-[10px] opacity-80">
                    {trimYear(tail, d.due_date)}
                  </span>
                </>
              )}
              <span className="opacity-50" aria-hidden="true">·</span>
              <span className="tabular-nums text-[10px] opacity-80">
                {d.estimated && (
                  <span data-testid={`deadline-estimated-${d.country_code || d.kind}`}>
                    <span aria-hidden="true">≈</span><span className="sr-only">estimated </span>
                  </span>
                )}
                {shortDate(d.due_date)}
              </span>
              <span className="opacity-50" aria-hidden="true">·</span>
              <span className="font-bold tabular-nums text-[10px]">{whenText(d)}</span>
            </span>
          </li>
        );
      })}
    </ul>
  );
};
